import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Box
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatErpDocumentNumber } from "../../utils/formatErpDocumentNumber";
import { formatErpMoney } from "../../utils/formatErpMoney";
import {
  getErpPaymentSummaryFromListRow,
  getFacturaSaldoFromListRow
} from "../../utils/erpDocumentPayment";
import ErpDocumentDetailDialog from "../ErpDocumentDetailDialog";
import { openErpDocumentInNewTab } from "../../utils/erpUrls";

const PAGE_SIZE = 10;

const CONFIG = {
  "facturas-venta": {
    title: "Facturas de venta",
    listPath: (clienteId) => `/erp/clientes/${clienteId}/facturas-venta`,
    detailPath: (id) => `/erp/facturas-venta/${id}`,
    erpPath: (id) => `/cuentas-por-cobrar/facturas-venta/${id}`,
    idField: "facturaVentaId",
    numberField: "numeroFactura",
    emptyMessage: "No hay facturas de venta para este cliente."
  },
  ordenes: {
    title: "Órdenes de venta",
    listPath: (clienteId) => `/erp/clientes/${clienteId}/ordenes`,
    detailPath: (id) => `/erp/ordenes/${id}`,
    erpPath: (id) => `/cuentas-por-cobrar/ordenes/${id}`,
    idField: "ordenId",
    numberField: "numeroOrden",
    emptyMessage: "No hay órdenes para este cliente."
  }
};

const ESTADO_LABEL = {
  PENDIENTE: "Pendiente",
  CONFIRMADA: "Confirmada",
  PROCESADA: "Procesada"
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 14,
    maxHeight: "min(90vh, 680px)",
    display: "flex",
    flexDirection: "column"
  },
  dialogContent: {
    flex: 1,
    overflow: "auto"
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(1)
  },
  tableWrap: {
    minHeight: 200
  },
  voidedRow: {
    opacity: 0.65
  },
  cobroPendiente: {
    color: "#c62828",
    fontWeight: 600
  },
  cobroPagado: {
    color: "#2e7d32",
    fontWeight: 500
  },
  cobroParcial: {
    color: "#e65100",
    fontWeight: 500
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 160
  }
}));

const formatDate = (value) => {
  if (!value) return "—";
  const str = String(value).split("T")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const [y, m, d] = str.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("es-NI", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const ErpDocumentListDialog = ({
  open,
  onClose,
  type,
  clienteId,
  clienteName,
  erpFrontendUrl
}) => {
  const classes = useStyles();
  const cfg = CONFIG[type];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [facturaSaldoById, setFacturaSaldoById] = useState(() => new Map());

  const loadFacturaSaldoMap = useCallback(async () => {
    if (!clienteId) return new Map();
    const map = new Map();
    let currentPage = 1;
    const limit = 50;
    for (let i = 0; i < 40; i += 1) {
      const { data } = await api.get(
        `/erp/clientes/${clienteId}/facturas-venta`,
        { params: { page: currentPage, limit } }
      );
      const batch = data.data || [];
      batch.forEach((f) => {
        if (f.facturaVentaId != null) {
          map.set(f.facturaVentaId, getFacturaSaldoFromListRow(f));
        }
      });
      if (batch.length < limit) break;
      const totalPages = data.pagination?.totalPages;
      if (typeof totalPages === "number" && currentPage >= totalPages) break;
      currentPage += 1;
    }
    return map;
  }, [clienteId]);

  const fetchList = useCallback(async () => {
    if (!clienteId || !cfg) return;
    setLoading(true);
    try {
      const saldoMapPromise =
        type === "ordenes" ? loadFacturaSaldoMap() : Promise.resolve(null);

      const { data } = await api.get(cfg.listPath(clienteId), {
        params: { page: page + 1, limit: PAGE_SIZE }
      });
      const list = data.data || [];
      setRows(list);
      setTotal(data.pagination?.total || 0);

      const saldoMap = await saldoMapPromise;
      if (type === "ordenes" && saldoMap) {
        setFacturaSaldoById(saldoMap);
      } else if (type === "facturas-venta") {
        const map = new Map();
        list.forEach((f) => {
          if (f.facturaVentaId != null) {
            map.set(f.facturaVentaId, getFacturaSaldoFromListRow(f));
          }
        });
        setFacturaSaldoById(map);
      }
    } catch (err) {
      toastError(err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [clienteId, cfg, page, type, loadFacturaSaldoMap]);

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open, type, clienteId]);

  useEffect(() => {
    if (open && clienteId) {
      fetchList();
    }
  }, [open, clienteId, page, fetchList]);

  const handleView = async (row) => {
    const id = row[cfg.idField];
    if (!id) return;
    setDetailLoading(true);
    setDetailOpen(true);
    setDetailItem(null);
    try {
      const { data } = await api.get(cfg.detailPath(id));
      setDetailItem(data.data || null);
    } catch (err) {
      toastError(err);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenInErp = (row) => {
    const id = row[cfg.idField];
    openErpDocumentInNewTab(erpFrontendUrl, type, id);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  if (!cfg) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle disableTypography>
          <Box className={classes.titleRow}>
            <Box>
              <Typography variant="h6">{cfg.title}</Typography>
              {clienteName && (
                <Typography variant="body2" color="textSecondary">
                  {clienteName}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          {loading ? (
            <Box className={classes.loadingBox}>
              <CircularProgress size={32} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography color="textSecondary" align="center" style={{ padding: 24 }}>
              {cfg.emptyMessage}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Número</TableCell>
                    <TableCell>Fecha</TableCell>
                    {type === "ordenes" && <TableCell>Estado</TableCell>}
                    {type === "facturas-venta" && <TableCell>Tipo</TableCell>}
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pagado</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Cobro</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const id = row[cfg.idField];
                    const voided = type === "facturas-venta" && row.anulado;
                    const cobro = getErpPaymentSummaryFromListRow(
                      row,
                      type,
                      facturaSaldoById
                    );
                    const cobroClass =
                      cobro?.statusLabel === "Pagada"
                        ? classes.cobroPagado
                        : cobro?.statusLabel === "Parcial"
                        ? classes.cobroParcial
                        : cobro?.statusLabel === "Anulada"
                        ? undefined
                        : classes.cobroPendiente;
                    return (
                      <TableRow
                        key={id}
                        hover
                        className={voided ? classes.voidedRow : undefined}
                      >
                        <TableCell>
                          {formatErpDocumentNumber(row[cfg.numberField], row.sucursalSede)}
                          {voided && (
                            <Typography variant="caption" color="error" display="block">
                              Anulada
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(row.fecha)}</TableCell>
                        {type === "ordenes" && (
                          <TableCell>
                            {ESTADO_LABEL[row.estado] || row.estado || "—"}
                          </TableCell>
                        )}
                        {type === "facturas-venta" && (
                          <TableCell>
                            {row.tipoFactura?.nombre || row.tipoFactura?.codigo || "—"}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          {formatErpMoney(row.total, row.tenantCurrency?.symbol)}
                        </TableCell>
                        <TableCell align="right">
                          {cobro && cobro.totalAbonado != null
                            ? formatErpMoney(
                                cobro.totalAbonado,
                                cobro.symbol
                              )
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          {cobro && cobro.saldoPendiente != null
                            ? formatErpMoney(
                                cobro.saldoPendiente,
                                cobro.symbol
                              )
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            className={cobroClass}
                            component="span"
                          >
                            {cobro?.statusLabel || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            title="Ver resumen en CRM"
                            onClick={() => handleView(row)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          {erpFrontendUrl && (
                            <IconButton
                              size="small"
                              title="Abrir detalle en el ERP"
                              onClick={() => handleOpenInErp(row)}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ErpDocumentDetailDialog
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailItem(null);
        }}
        type={type}
        item={detailItem}
        loading={detailLoading}
        erpFrontendUrl={erpFrontendUrl}
      />
    </>
  );
};

export default ErpDocumentListDialog;
